// Voice Provider Interface and Types
// This provides a unified interface for different voice/call agent providers

export interface TranscriptSegment {
	speaker: 'agent' | 'caller'
	text: string
	timestamp?: number
}

export interface TranscriptEvent {
	segments: TranscriptSegment[]
	fullTranscript: string
	lastSegment: TranscriptSegment
}

export interface ConnectionState {
	isConnected: boolean
	isConnecting: boolean
	isRecording: boolean
	isMuted: boolean
	conversationId: string | null
	currentPhase?: string
	progress?: number
}

export interface ProviderConfig {
	debug?: boolean
	customGreeting?: string
	additionalPrompt?: string
	sampleRate?: number
	// Provider-specific options can be added here
	[key: string]: unknown
}

export interface ICSRFieldUpdate {
	field: string
	value: string | boolean | number
	confidence?: number
}

export interface VoiceProviderEvents {
	onTranscript?: (event: TranscriptEvent) => void
	onStateChange?: (state: ConnectionState) => void
	onDataExtracted?: (data: Record<string, unknown>) => void
	onError?: (error: Error) => void
	onFieldUpdate?: (update: ICSRFieldUpdate) => void
}

export interface IVoiceAgentProvider {
	// Provider identification
	getProviderName(): string
	getProviderId(): string

	// Connection management
	connect(config?: ProviderConfig): Promise<string>
	disconnect(): Promise<void>
	isSupported(): Promise<boolean>

	// State queries
	getConnectionState(): ConnectionState
	isConnected(): boolean
	isRecording(): boolean
	isMuted(): boolean

	// Audio controls
	mute(): void
	unmute(): void
	toggleMute(): void

	// Event management
	setEventListeners(events: VoiceProviderEvents): void
	removeEventListeners(): void

	// Transcript access
	getFullTranscript(): string
	getTranscriptSegments(): TranscriptSegment[]

	// Cleanup
	destroy(): void
}

// Provider-specific error class
export class VoiceProviderError extends Error {
	constructor(
		message: string,
		public provider: string,
		public code?: string,
		public originalError?: unknown,
	) {
		super(message)
		this.name = 'VoiceProviderError'
	}
}

// Common provider states
export type ProviderPhase =
	| 'idle'
	| 'connecting'
	| 'greeting'
	| 'collecting_reporter_info'
	| 'collecting_patient_info'
	| 'collecting_event_details'
	| 'collecting_drug_info'
	| 'reviewing_details'
	| 'completing'
	| 'completed'
	| 'error'

export const ProviderPhases: Record<
	Uppercase<Exclude<ProviderPhase, ''>> | 'COMPLETED' | 'ERROR',
	ProviderPhase
> = {
	IDLE: 'idle',
	CONNECTING: 'connecting',
	GREETING: 'greeting',
	COLLECTING_REPORTER_INFO: 'collecting_reporter_info',
	COLLECTING_PATIENT_INFO: 'collecting_patient_info',
	COLLECTING_EVENT_DETAILS: 'collecting_event_details',
	COLLECTING_DRUG_INFO: 'collecting_drug_info',
	REVIEWING_DETAILS: 'reviewing_details',
	COMPLETING: 'completing',
	COMPLETED: 'completed',
	ERROR: 'error',
}

// Provider types
export type ProviderType = 'retell' | 'custom'

// Factory function type
export type ProviderFactory = (type: ProviderType) => IVoiceAgentProvider
